import { PatientRouting } from '@dataTypes/dicom-capture.type'

export const PATIENT_ROUTING_OPTIONS: {
  label: string
  value: PatientRouting
}[] = [
  { label: 'From DICOM tag (PatientID)', value: 'from_dicom_tag' },
  { label: 'Force "Unassigned" inbox', value: 'force_unassigned_inbox' },
]

export const patientRoutingLabel = (value: PatientRouting): string =>
  PATIENT_ROUTING_OPTIONS.find((o) => o.value === value)?.label ?? value

/**
 * Resolve an operatory id to its display name. Falls back to a short id snippet
 * when the operatory list hasn't loaded or the row points at a deleted chair.
 */
export const operatoryLabel = (
  operatoryId: string | null | undefined,
  operatories: { operatory_id: string; name: string }[],
): string => {
  if (!operatoryId) return '—'
  const match = operatories.find((o) => o.operatory_id === operatoryId)
  return match?.name ?? `Operatory ${operatoryId.slice(0, 8)}`
}

const NO_OPERATORY = '__none__'

export const buildOperatoryOptions = (
  operatories: { operatory_id: string; name: string }[],
): { label: string; value: string }[] => [
  { label: 'Not assigned to a chair', value: NO_OPERATORY },
  ...operatories.map((op) => ({ label: op.name, value: op.operatory_id })),
]

export const toOperatoryOption = (operatoryId: string): string =>
  operatoryId || NO_OPERATORY

export const fromOperatoryOption = (value: string): string =>
  value === NO_OPERATORY ? '' : value

/** AE title: uppercase alphanumeric (plus space/_/-), 1-16 chars. */
export const AE_TITLE_REGEX = /^[A-Z0-9 _-]{1,16}$/

/** Validate a comma/space-separated list of IPv4 addresses or CIDR blocks. */
export const isValidIpCidrList = (raw: string): boolean => {
  const tokens = raw
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean)
  if (tokens.length === 0) return true
  return tokens.every((token) => {
    const slash = token.indexOf('/')
    const base = slash === -1 ? token : token.slice(0, slash)
    if (slash !== -1) {
      const bits = Number(token.slice(slash + 1))
      if (!Number.isInteger(bits) || bits < 0 || bits > 32) return false
    }
    const octets = base.split('.')
    if (octets.length !== 4) return false
    return octets.every((o) => {
      const n = Number(o)
      return /^\d+$/.test(o) && Number.isInteger(n) && n >= 0 && n <= 255
    })
  })
}

/** Human-readable "seen 2 min ago" / "never connected" from an ISO timestamp. */
export const formatLastSeen = (iso: string | null): string => {
  if (!iso) return 'Never connected'
  const ts = new Date(iso).getTime()
  if (Number.isNaN(ts)) return 'Never connected'
  const diffMs = Date.now() - ts
  if (diffMs < 0) return 'just now'
  const sec = Math.floor(diffMs / 1000)
  if (sec < 60) return `seen ${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `seen ${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `seen ${hr}h ago`
  const days = Math.floor(hr / 24)
  return `seen ${days}d ago`
}

/** Green when seen within `freshMs` (default 10 min), otherwise grey/red. */
export const isRecentlySeen = (
  iso: string | null,
  freshMs = 10 * 60 * 1000,
): boolean => {
  if (!iso) return false
  const ts = new Date(iso).getTime()
  if (Number.isNaN(ts)) return false
  return Date.now() - ts <= freshMs
}
